import {Injectable} from '@angular/core';
import {MmDbService} from '../../../mm-db/mm-db-service';
import {MmProjectDbCollections} from '../../../mm-db/mm-project-db-schema';
import {FaultPatch} from './fault-patch';
import {resolveSequentially} from '@mydocma/common/src/lib/promise';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {FaultOperationPathEnum} from './fault-operation-path.enum';
import {MmPatchOperation} from '@docma-mm-core/patch/mm-patch-operation';
import {Guid} from 'guid-typescript';

@Injectable()
export class FaultPatchService {


  constructor(private readonly mmDbService: MmDbService) {

  }

  public async save(mydocmaUid: string, faultPatches: Array<FaultPatch>): Promise<Array<FaultPatch>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const setCommands = faultPatches.map(async (fp) => await db.set(MmProjectDbCollections.fault_patches as never, fp));
    await resolveSequentially(setCommands);
    return faultPatches;
  }

  public async saveAll(mydocmaUid: string, faultPatches: Array<FaultPatch>): Promise<Array<FaultPatch>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const setCommands =  await db.putAll(MmProjectDbCollections.fault_patches as never, faultPatches);
    return faultPatches;
  }

  public async moveToBackupAll(mydocmaUid: string, faultPatches: Array<FaultPatch>): Promise<Array<FaultPatch>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);

    const allPatches: FaultPatch[] = await db.getAll(MmProjectDbCollections.fault_patches as never)
    const patchesDict = this.groupByOperationId(allPatches);
    const backupPatches: FaultPatch[] = [];

    for (const p of faultPatches) {
      if (patchesDict.hasOwnProperty(p.operation.id)) {
        const mp = patchesDict[p.operation.id];
        backupPatches.push(mp[0]);
      }
    }

    return faultPatches;
  }



  public async remove(mydocmaUid: string, ids: Array<string>): Promise<unknown> {
    const dbService =  await this.mmDbService.getProjectDb(mydocmaUid);
    const idb = await dbService.db;
    const transaction = idb.transaction(MmProjectDbCollections.fault_patches as never, 'readwrite');
    return await resolveSequentially(ids.map(id => transaction.store.delete(id)));
  }

  public async getByFaultUid(mydocmaUid: string, fUid: string): Promise<Array<FaultPatch>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    const patches = await db.getAllFromIndex(MmProjectDbCollections.fault_patches as never, 'fUid', fUid);
    return patches.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  public async getByGroupUid(mydocmaUid: string, gUid: string): Promise<Array<FaultPatch>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    const patches = await db.getAllFromIndex(MmProjectDbCollections.fault_patches as never, 'gUid', gUid);
    return patches.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  public async getAllKeys(mydocmaUid: string): Promise<Array<IDBValidKey>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    return await db.getAllKeys(MmProjectDbCollections.fault_patches as never);
  }

  public async getAll(mydocmaUid: string): Promise<Array<FaultPatch>> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    return await db.getAll(MmProjectDbCollections.fault_patches as never);
  }

  public generateOperationFromChangeState(path: FaultOperationPathEnum, changeState: ChangeState<any>): MmPatchOperation {
    if (!changeState || changeState.status === ChangeStatusEnum.NotChanged) {
      // TODO: Log warnung
      return null;
    }
    if (changeState.status === ChangeStatusEnum.New) {
      return {
        id: Guid.create().toString(),
        op: 'add',
        path,
        value: changeState.data
      };

    } else {
      return {
        id: Guid.create().toString(),
        op: 'replace',
        path,
        value: changeState.data
      };

    }
  }


public groupByOuid(patches: Array<FaultPatch>): { [ouid: string]: Array<FaultPatch> } {
  return patches
    .sort((prev, curr) => +prev?.date - +curr?.date)
    .reduce((acc, curr) => {
      const oUid = curr.oUid;

      if (acc[oUid] == null) {
        acc[oUid] = [];
      }

      acc[oUid].push(curr);

      return acc;
    }, {});
}

  public groupByOperationId(patches: Array<FaultPatch>): { [ouid: string]: Array<FaultPatch> } {
    return patches
      .sort((prev, curr) => +prev?.date - +curr?.date)
      .reduce((acc, curr) => {
        const oid = curr.operation.id;

        if (acc[oid] == null) {
          acc[oid] = [];
        }

        acc[oid].push(curr);

        return acc;
      }, {});
  }

public groupByPuid(patches: Array<FaultPatch>): { [puid: string]: Array<FaultPatch> } {
  return patches
    .sort((prev, curr) => +prev?.date - +curr?.date)
    .reduce((acc, curr) => {
      const pUid = curr.pUid;

      if (acc[pUid] == null) {
        acc[pUid] = [];
      }

      acc[pUid].push(curr);

      return acc;
    }, {});
}

  public groupByFuid(patches: Array<FaultPatch>): { [fuid: string]: Array<FaultPatch> } {
    return patches
      .sort((prev, curr) => +prev?.date - +curr?.date)
      .reduce((acc, curr) => {
        const fUid = curr.fUid;

        if (acc[fUid] == null) {
          acc[fUid] = [];
        }

        acc[fUid].push(curr);

        return acc;
      }, {});
  }


  public generateChangeStateFromPatches(patches: FaultPatch[]): ChangeState<any> {
    let data = {};
    let status = ChangeStatusEnum.NotChanged;
    let changeDate: Date;
    const ps = [...patches];

    if (ps.length > 0) {
      if (ps[0].operation.op === 'add') {
        status = ChangeStatusEnum.New;
        changeDate = ps[0].date;
        data = ps[0].operation.value;
        ps.splice(0, 1);
      } else {
        status = ChangeStatusEnum.Changed;
        changeDate = patches[ps.length - 1].date;
      }
    }

    ps.forEach(p => {
      if (p.operation.op === 'add' || p.operation.op === 'replace' ) {
        Object.assign(data, p.operation.value);
      }
    });

    return new ChangeState({
      status,
      date: changeDate,
      data
     });
  }
}

export function faultUidsOfPatches(allPatches: FaultPatch[]) {
  const patchedFaultUids: string[] = [];

  for (const patch of (allPatches ?? [])) {
    if (patch.fUid && patchedFaultUids.findIndex(x => x === patch.fUid) === -1) {
      patchedFaultUids.push(patch.fUid);
    }
  }
  return patchedFaultUids;
}

export function patchesByFUid(allPatches: FaultPatch[]) {
  const groupedPatches: {[fuid: string]: FaultPatch[]} = {};

  for (const patch of (allPatches ?? [])) {
    if (patch.fUid) {
      if (!groupedPatches.hasOwnProperty(patch.fUid)) {
        groupedPatches[patch.fUid] = [patch];
      }
      else {
        groupedPatches[patch.fUid].push(patch);
      }
    }
  }
  return groupedPatches;
}

export function patchesByGUid(allPatches: FaultPatch[]) {
  const groupedPatches: {[guid: string]: FaultPatch[]} = {};

  for (const patch of (allPatches ?? [])) {
    if (patch.gUid) {
      if (!groupedPatches.hasOwnProperty(patch.gUid)) {
        groupedPatches[patch.gUid] = [patch];
      }
      else {
        groupedPatches[patch.gUid].push(patch);
      }
    }
  }
  return groupedPatches;
}
